import { useState } from 'react';
import { ConfirmModal } from '@/components/ConfirmModal';
import './JobTypeChipSelector.css';

interface Props {
  jobTypes: Array<{ id: string; name: string }>;
  /** Either ["*"] (all) or one-or-more job-type ids. */
  selectedIds: string[];
  /** When true, scope changes show a confirm dialog. */
  hasExistingPermissions: boolean;
  onChange: (ids: string[]) => void;
}

export function JobTypeChipSelector({
  jobTypes, selectedIds, hasExistingPermissions, onChange,
}: Props) {
  const isAll = selectedIds.length === 1 && selectedIds[0] === '*';
  const [pendingChange, setPendingChange] = useState<string[] | null>(null);

  function withConfirm(next: string[]) {
    if (hasExistingPermissions) {
      setPendingChange(next);
      return;
    }
    onChange(next);
  }

  function toggleAll() {
    if (isAll) return;  // already all — no-op
    withConfirm(['*']);
  }

  function toggleJobType(id: string) {
    let next: string[];
    if (isAll) {
      next = [id];  // switching from All to a specific chip
    } else if (selectedIds.includes(id)) {
      next = selectedIds.filter((s) => s !== id);
      if (next.length === 0) next = ['*'];  // empty → snap back to All
    } else {
      next = [...selectedIds, id];
    }
    withConfirm(next);
  }

  return (
    <div className="jtc-row">
      <span className="rb-label">Applies to job types:</span>
      <button
        type="button"
        className={`jtc-chip ${isAll ? 'jtc-chip--active' : ''}`}
        aria-pressed={isAll}
        onClick={toggleAll}
      >
        All
      </button>
      {jobTypes.map((jt) => {
        const active = !isAll && selectedIds.includes(jt.id);
        return (
          <button
            key={jt.id}
            type="button"
            className={`jtc-chip ${active ? 'jtc-chip--active' : ''}`}
            aria-pressed={active}
            onClick={() => toggleJobType(jt.id)}
          >
            {jt.name}
          </button>
        );
      })}
      <ConfirmModal
        open={pendingChange !== null}
        title="Rescope role permissions?"
        description="This will rescope the role's existing permissions. Existing grants on the previous scope stay assigned but no longer apply to documents in the new scope."
        confirmLabel="Rescope"
        onConfirm={() => {
          if (pendingChange) onChange(pendingChange);
          setPendingChange(null);
        }}
        onCancel={() => setPendingChange(null)}
      />
    </div>
  );
}
